/**
 * useBodyScrollLock Hook
 * 
 * 모달/팝업이 열렸을 때 배경 스크롤을 방지하는 훅
 */

import { useEffect } from 'react';

type ScrollLockSnapshot = {
  scrollY: number;
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
  bodyPaddingRight: string;
  htmlOverflow: string;
};

// 여러 모달이 동시에 열리는 케이스를 위해 전역(모듈) 단위로 중첩 잠금을 관리한다.
let lockCount = 0;
let snapshot: ScrollLockSnapshot | null = null;
let hasInstalledRecoveryListeners = false;

function getScrollbarWidth(): number {
  // 스크롤바가 없는 환경에서는 0이 된다.
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function clearLockStyles(): void {
  const body = document.body;
  const html = document.documentElement;

  html.style.overflow = '';
  body.style.overflow = '';
  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  body.style.paddingRight = '';
}

function maybeRecoverStuckLock(): void {
  // 잠금 카운트가 0인데도 인라인 스타일이 잠김 상태로 남아 있으면 복구한다.
  // (크래시/리액트 언마운트 꼬임/BFCache/브라우저 버그 등)
  if (lockCount !== 0 || snapshot) return;
  const body = document.body;
  if (body.style.position !== 'fixed' || body.style.overflow !== 'hidden') return;

  const top = body.style.top || '';
  // 기존 로직과 동일하게 top이 "-123px" 형태면 해당 위치로 복원
  const match = top.match(/^-(\d+)px$/);
  const scrollY = match ? Number(match[1]) : 0;

  clearLockStyles();
  window.scrollTo(0, scrollY);
}

function lockBodyScroll(): void {
  lockCount += 1;
  if (lockCount > 1) return;

  const body = document.body;
  const html = document.documentElement;
  const scrollY = window.scrollY || window.pageYOffset || 0;

  snapshot = {
    scrollY,
    bodyOverflow: body.style.overflow,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyWidth: body.style.width,
    bodyPaddingRight: body.style.paddingRight,
    htmlOverflow: html.style.overflow,
  };

  const scrollbarWidth = getScrollbarWidth();
  if (scrollbarWidth > 0) {
    // 스크롤바가 사라지며 레이아웃이 흔들리는 것을 완화
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  // html/body 모두 잠금 (브라우저별 스크롤 컨테이너 차이 대응)
  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.width = '100%';
}

function unlockBodyScroll(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount !== 0) return;
  if (!snapshot) return;

  const body = document.body;
  const html = document.documentElement;

  // 원래 스타일 복원
  html.style.overflow = snapshot.htmlOverflow;
  body.style.overflow = snapshot.bodyOverflow;
  body.style.position = snapshot.bodyPosition;
  body.style.top = snapshot.bodyTop;
  body.style.width = snapshot.bodyWidth;
  body.style.paddingRight = snapshot.bodyPaddingRight;

  // 스크롤 위치 복원
  window.scrollTo(0, snapshot.scrollY);

  snapshot = null;
}

// BFCache(뒤로가기/앞으로가기) 복원 시, 일부 환경에서 body가 fixed/hidden 상태로 남는 케이스가 있어
// persisted 복원일 때는 잠금을 강제 해제한다.
if (typeof window !== 'undefined') {
  // 개발 환경(HMR)에서도 리스너가 중복 등록되지 않도록 가드
  if (!hasInstalledRecoveryListeners) {
    hasInstalledRecoveryListeners = true;

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      // BFCache 복원(persisted) 뿐 아니라, 초기 진입에서도 "잠금이 남은" 케이스를 복구
      if (event.persisted) {
        lockCount = 0;
        snapshot = null;
        clearLockStyles();
        return;
      }
      maybeRecoverStuckLock();
    });

    // 최초 로드 직후에도 한 번 더 복구 시도
    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', () => maybeRecoverStuckLock(), { once: true });
    } else {
      // 이미 로드된 경우(예: HMR) 즉시 시도
      maybeRecoverStuckLock();
    }
  }
}

export function useBodyScrollLock(isLocked: boolean): void {
  useEffect(() => {
    if (!isLocked) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isLocked]);
}

export default useBodyScrollLock;

